import { attribution, boundedMessage, createCaptureSession, renderConsentRenewal } from './proda-capture-client.js';
const FORM_SELECTOR = 'form[data-klaviyo-capture]';
const production = document.body?.dataset.storefrontEnvironment === 'production';
function fieldValue(form, name) { return String(form.elements.namedItem(name)?.value || '').trim(); }
function checked(form, selector) { const field = form.querySelector(selector); return field?.checked === true && !field.disabled; }
function validPhone(value) { const digits = value.replace(/\D/g, ''); return /^[+()\d .-]+$/.test(value) && (digits.length === 10 || digits.length === 11 && digits.startsWith('1')); }
export function contactClassification(form) {
  const topic = fieldValue(form, 'contact[Request type]');
  const subtype = fieldValue(form, 'contact[Business type]');
  const creator = fieldValue(form, 'contact[Creator type]');
  const roles = { creator: creator === 'Athlete' ? 'athlete' : creator === 'Creator or influencer' ? 'creator' : undefined, event: 'event_partner', business: ['agency', 'investor'].includes(subtype) ? subtype : 'business' };
  const consumer = checked(form, '[data-consumer-interest]') || ['order', 'product'].includes(topic);
  return {
    consumerInterest: consumer,
    ...(roles[topic] ? { consumerRole: roles[topic] } : consumer ? { consumerRole: 'consumer' } : {}),
    intakeType: topic === 'business' && ['agency', 'investor'].includes(subtype) ? subtype : ['creator', 'event', 'business', 'general'].includes(topic) ? topic : 'general',
  };
}
function statusNode(form) {
  let node = form.querySelector('[data-proda-capture-status]');
  if (!node) { node = document.createElement('div'); node.dataset.prodaCaptureStatus = 'true'; node.className = 'proda-capture-status'; node.setAttribute('role', 'status'); node.setAttribute('aria-live', 'polite'); form.append(node); }
  return node;
}
export function initializeCaptureForm(form) {
  if (form.dataset.captureInitialized === 'true') return;
  form.dataset.captureInitialized = 'true';
  const newsletter = form.dataset.klaviyoCapture === 'newsletter';
  const scope = newsletter ? 'newsletter' : 'contact';
  const session = createCaptureSession(scope);
  const returnedContact = !newsletter && Boolean(form.querySelector('.proda-service__status, .contact-form__success'));
  // A new service request gets a new capture attempt; the durable backend owns previous pending work.
  if (!newsletter && !returnedContact) session.reset();
  let busy = false;
  let editable = false;
  function busyState(value) {
    busy = value;
    form.dataset.klaviyoSyncing = String(value);
    form.querySelectorAll('button[type="submit"],input[type="submit"],.email-signup__button').forEach(button => { button.disabled = value; });
    if (value) form.setAttribute('aria-busy', 'true'); else form.removeAttribute('aria-busy');
  }
  function render(result, uncertain = false) {
    const node = statusNode(form); node.replaceChildren();
    if (result?.requiresNewConsent && renderConsentRenewal(node, result, check)) return;
    editable = ['email', 'sms', 'not_found'].includes(result?.step);
    if (editable) { node.textContent = boundedMessage(result?.error, 'We haven’t received that signup. Check your details and submit again.'); return; }
    if (result?.step === 'complete') {
      node.textContent = newsletter ? 'You’re on the list. Keep an eye on your inbox for PRODA news and offers.' : 'Your selected PRODA updates are saved.';
      if (newsletter) form.querySelector('[name="contact[email]"]')?.setAttribute('data-capture-complete', 'true');
    } else {
      node.textContent = uncertain ? 'We couldn’t check your signup yet. Check its status before sending it again.' : result?.step === 'pending' ? 'Your selected updates are processing. Check below in a moment. No need to enter your details again.' : boundedMessage(result?.message, 'We couldn’t finish your signup. Your existing preferences have not been changed.');
      const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Check signup status';
      retry.addEventListener('click', () => void check()); node.append(retry);
    }
    if (result?.error) { const error = document.createElement('p'); error.textContent = boundedMessage(result.error); node.append(error); }
  }
  async function check(payload = { action: 'status' }) {
    if (busy) return; busyState(true);
    try { render(await session.request(payload)); } catch { render(null, true); }
    finally { busyState(false); }
  }
  form.addEventListener('submit', async event => {
    if (busy) { event.preventDefault(); return; }
    const emailConsent = newsletter || checked(form, '[data-klaviyo-email-consent]');
    const smsConsent = !newsletter && checked(form, '[data-klaviyo-sms-consent]');
    if (!emailConsent && !smsConsent) return;
    event.preventDefault();
    if (smsConsent) {
      const phone = form.elements.namedItem('contact[phone]');
      phone?.setCustomValidity(validPhone(fieldValue(form, 'contact[phone]')) ? '' : 'Enter a valid mobile number for text updates.');
    }
    if (!form.reportValidity()) return;
    if (!production) { statusNode(form).textContent = 'Preview mode: no contact or marketing data was submitted.'; return; }
    busyState(true);
    const payload = newsletter
      ? { action: 'email', kind: 'newsletter', email: fieldValue(form, 'contact[email]'), consent: true, source: 'footer_newsletter', attribution: attribution() }
      : { action: 'subscribe', kind: 'contact_optin', source: 'contact_form', emailConsent, smsConsent, ...(emailConsent ? { email: fieldValue(form, 'contact[email]') } : {}), ...(smsConsent ? { phone: fieldValue(form, 'contact[phone]') } : {}), ...contactClassification(form), attribution: attribution() };
    try {
      // A returned receipt or persisted captureId provides safe recovery after a lost response.
      const result = await session.request(session.attempted && !editable ? { action: 'status' } : payload);
      render(result);
    } catch { render(null, true); }
    finally {
      busyState(false);
      if (!newsletter) {
        // Marketing cannot block service. Shopify remains the contact transport, independently of signup outcome.
        HTMLFormElement.prototype.submit.call(form);
      }
    }
  });
  form.addEventListener('input', event => { event.target?.setCustomValidity?.(''); if (newsletter && session.complete) { session.reset(); statusNode(form).replaceChildren(); } });
  if (newsletter && session.complete) render({ step: 'complete' });
  // A returned Shopify contact page can report pending signup independently from request submission.
  if (returnedContact && session.attempted) void check();
}
if (typeof document !== 'undefined') {
  document.querySelectorAll(FORM_SELECTOR).forEach(initializeCaptureForm);
  document.addEventListener('shopify:section:load', event => {
    const root = event.target;
    if (!(root instanceof Element) && root !== document) return;
    if (root instanceof HTMLFormElement && root.matches(FORM_SELECTOR)) initializeCaptureForm(root);
    root.querySelectorAll(FORM_SELECTOR).forEach(initializeCaptureForm);
  });
}
